import React, { useEffect, useMemo, useState } from "react";
import { Image } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MetaData from "../../components/layout/MetaData";
import Breadcrumb from "../../components/layout/Breadcrumb/breadcrumbCart";
import { getCartItemKey, getCartItemLink } from "../../helpers/cartItem";
import { removeCartItem, setCartItem } from "../../redux/features/cartSlice";
import { formatThousandsNumber } from "../../utils/numberFormat";
import "./CartPage.css";

/**
 * 格式化购物车金额。
 * @param {number} value 金额
 * @returns {string} 两位小数金额
 */
const formatPrice = (value) => formatThousandsNumber(Number(value || 0).toFixed(2));

/**
 * 根据库存字段生成可用性文案。
 * @param {string|number} stock 库存值
 * @returns {string} 可用性文案
 */
const getAvailabilityText = (stock) => {
  const stockQuantity = Number(stock);
  if (Number.isFinite(stockQuantity)) {
    return stockQuantity > 0 ? `${formatThousandsNumber(stockQuantity)} In Stock` : "Out of Stock";
  }
  return stock || "Available";
};

/**
 * 获取购物车商品的 CTYEA Part Number。
 * @param {Object} item 购物车商品
 * @returns {string} CTYEA Part Number
 */
const getCtyeaPartNumber = (item = {}) => item?.CTYEA_Part_Number || "";

/**
 * DigiKey 风格的购物车页面，保留项目原有本地购物车与结账流程。
 * @returns {JSX.Element} 购物车页面
 */
const Cart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartItems } = useSelector((state) => state.cart);
  const [selectedItemKeys, setSelectedItemKeys] = useState(new Set());

  const subtotal = useMemo(
    () => cartItems.reduce(
      (total, item) => total + Number(item?.quantity || 0) * Number(item?.price || 0),
      0
    ),
    [cartItems]
  );
  const estimatedShipping = subtotal > 200 ? 0 : 25;
  const estimatedTotal = subtotal + estimatedShipping;
  const allItemsSelected = cartItems.length > 0 && selectedItemKeys.size === cartItems.length;

  /** 商品变化后移除已经不存在的选择项。 */
  useEffect(() => {
    setSelectedItemKeys((currentKeys) => {
      const availableKeys = new Set(cartItems.map((item) => getCartItemKey(item)));
      return new Set([...currentKeys].filter((itemKey) => availableKeys.has(itemKey)));
    });
  }, [cartItems]);

  /**
   * 删除单个购物车商品。
   * @param {Object} item 购物车商品
   */
  const removeCartItemHandler = (item) => dispatch(removeCartItem(getCartItemKey(item)));

  /** 跳转到项目原有的收货信息页面。 */
  const handleCheckout = () => navigate("/shipping");

  /**
   * 更新购物车商品数量并保留扩展字段。
   * @param {Object} item 购物车商品
   * @param {number} newQty 新数量
   */
  const setItemToCart = (item, newQty) => {
    dispatch(setCartItem({
      ...item,
      quantity: newQty,
      multipleOrderQuantity: item?.multipleOrderQuantity || 1,
      weight: item?.weight ?? item?.Weight,
    }));
  };

  /**
   * 按最小订购倍数增加数量。
   * @param {Object} item 购物车商品
   */
  const increaseQty = (item) => {
    const moq = Number(item?.multipleOrderQuantity || 1);
    setItemToCart(item, Number(item?.quantity || moq) + moq);
  };

  /**
   * 按最小订购倍数减少数量。
   * @param {Object} item 购物车商品
   */
  const decreaseQty = (item) => {
    const moq = Number(item?.multipleOrderQuantity || 1);
    const newQty = Number(item?.quantity || moq) - moq;
    if (newQty >= moq) setItemToCart(item, newQty);
  };

  /**
   * 规范化手工输入数量。
   * @param {Object} item 购物车商品
   * @param {React.ChangeEvent<HTMLInputElement>} event 输入事件
   */
  const handleQuantityChange = (item, event) => {
    const moq = Number(item?.multipleOrderQuantity || 1);
    const inputQuantity = Number.parseInt(event.target.value, 10);
    const quantity = Number.isNaN(inputQuantity) || inputQuantity < moq
      ? moq
      : Math.ceil(inputQuantity / moq) * moq;
    setItemToCart(item, quantity);
  };

  /**
   * 切换单个商品选择状态。
   * @param {string} itemKey 商品唯一键
   */
  const toggleItemSelection = (itemKey) => {
    setSelectedItemKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys);
      nextKeys.has(itemKey) ? nextKeys.delete(itemKey) : nextKeys.add(itemKey);
      return nextKeys;
    });
  };

  /**
   * 全选或取消全选。
   * @param {boolean} checked 是否全选
   */
  const toggleAllItems = (checked) => {
    setSelectedItemKeys(
      checked ? new Set(cartItems.map((item) => getCartItemKey(item))) : new Set()
    );
  };

  /** 删除当前选中的购物车商品。 */
  const removeSelectedItems = () => {
    selectedItemKeys.forEach((itemKey) => dispatch(removeCartItem(itemKey)));
    setSelectedItemKeys(new Set());
  };

  return (
    <article className="cart-page">
      <MetaData title="Shopping Cart" />
      <div className="cart-page__container">
        <Breadcrumb />
        <div className="cart-notice" role="status">
          <i className="fas fa-info-circle" aria-hidden="true"></i>
          <span><strong>Attention</strong> — Order processing time and final shipping cost will be confirmed during checkout.</span>
        </div>

        <div className="cart-title-row">
          <div>
            <h1>Shopping Cart</h1>
            <p>{cartItems.length} item(s) in your cart</p>
          </div>
          <Link className="cart-secondary-link" to="/products">
            <i className="fas fa-plus" aria-hidden="true"></i> Add More Products
          </Link>
        </div>

        {cartItems.length === 0 ? (
          <section className="cart-empty-state">
            <i className="fas fa-shopping-cart" aria-hidden="true"></i>
            <h2>Your shopping cart is empty.</h2>
            <p>Browse our product catalog to find the parts you need.</p>
            <Link className="cart-primary-button" to="/products">Browse Products</Link>
          </section>
        ) : (
          <div className="cart-layout">
            <main className="cart-products">
              <div className="cart-warning" role="status">
                <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
                Product availability is based on the latest cart data and may change before checkout.
              </div>

              <div className="cart-table-header">
                <div className="cart-select-cell">
                  <input
                    type="checkbox"
                    aria-label="Select all cart items"
                    checked={allItemsSelected}
                    onChange={(event) => toggleAllItems(event.target.checked)}
                  />
                </div>
                <strong className="cart-product-heading">Product Details</strong>
                <strong>Quantity</strong>
                <strong>Availability</strong>
                <strong>Unit Price</strong>
                <strong>Extended Price</strong>
              </div>

              <div className="cart-item-list">
                {cartItems.map((item, index) => {
                  const itemKey = getCartItemKey(item);
                  const itemLink = getCartItemLink(item);
                  const imageSource = Array.isArray(item?.image) ? item.image[0] : item?.image;
                  const ctyeaPartNumber = getCtyeaPartNumber(item);
                  const partNumber = item?.Manufacturer_Part_Number
                    || item?.CTYEA_Part_Number
                    || item?.partNumber
                    || (item?.isQuoteItem ? "Custom quote item" : item?.product);
                  const productDescription = item?.description || partNumber;

                  return (
                    <section className="cart-item" key={itemKey}>
                      <div className="cart-select-cell">
                        <input
                          type="checkbox"
                          aria-label={`Select ${item?.name || `item ${index + 1}`}`}
                          checked={selectedItemKeys.has(itemKey)}
                          onChange={() => toggleItemSelection(itemKey)}
                        />
                        <span>{index + 1}</span>
                      </div>

                      <div className="cart-product-cell">
                        <Image src={imageSource} className="cart-product-image" alt={item?.name || "Product"} />
                        <div className="cart-product-info">
                          {itemLink ? (
                            <Link to={itemLink} className="cart-product-name">{item?.name}</Link>
                          ) : (
                            <strong className="cart-product-name">{item?.name}</strong>
                          )}
                          {ctyeaPartNumber ? (
                            <span>CTYEA Part Number: {ctyeaPartNumber}</span>
                          ) : null}
                          <span>{item?.Manufacturer || item?.manufacturer || "Creatyea"}</span>
                          <span className="cart-product-description">{productDescription}</span>
                          {item?.bomOrderId ? (
                            <Link to={`/my-parts/${item.bomOrderId}`}>
                              Source BOM · line(s) {item.bomLineNo || "-"}
                            </Link>
                          ) : null}
                          {item?.isQuoteItem && item?.quoteSnapshot?.sizeLabel ? (
                            <small>{item.quoteSnapshot.sizeLabel} / {item.quoteSnapshot.layers} Layers</small>
                          ) : null}
                        </div>
                      </div>

                      <div className="cart-quantity-cell" data-label="Quantity">
                        <div className="cart-quantity-control">
                          <button
                            type="button"
                            aria-label={`Decrease ${item?.name || "item"} quantity`}
                            onClick={() => decreaseQty(item)}
                            disabled={Number(item?.quantity) <= Number(item?.multipleOrderQuantity || 1)}
                          >−</button>
                          <input
                            type="number"
                            aria-label={`${item?.name || "Item"} quantity`}
                            step={item?.multipleOrderQuantity || 1}
                            min={item?.multipleOrderQuantity || 1}
                            value={item?.quantity}
                            onChange={(event) => handleQuantityChange(item, event)}
                          />
                          <button
                            type="button"
                            aria-label={`Increase ${item?.name || "item"} quantity`}
                            onClick={() => increaseQty(item)}
                          >+</button>
                        </div>
                        {Number(item?.multipleOrderQuantity || 1) > 1 ? (
                          <small>Multiple of {item.multipleOrderQuantity}</small>
                        ) : null}
                      </div>

                      <div className="cart-availability-cell" data-label="Availability">
                        <span>
                          <i className={`fas ${Number(item?.stock) === 0 ? "fa-times-circle" : "fa-check-circle"}`} aria-hidden="true"></i>
                          {getAvailabilityText(item?.stock)}
                        </span>
                      </div>
                      <div className="cart-price-cell" data-label="Unit Price">${formatPrice(item?.price)}</div>
                      <div className="cart-extended-cell" data-label="Extended Price">
                        <strong>${formatPrice(Number(item?.price) * Number(item?.quantity))}</strong>
                        <button type="button" onClick={() => removeCartItemHandler(item)}>
                          <i className="fas fa-trash-alt" aria-hidden="true"></i> Remove
                        </button>
                      </div>
                    </section>
                  );
                })}
              </div>

              <div className="cart-bulk-actions">
                <button type="button" disabled={selectedItemKeys.size === 0} onClick={removeSelectedItems}>
                  <i className="fas fa-trash-alt" aria-hidden="true"></i>
                  Remove Selected ({selectedItemKeys.size})
                </button>
                <Link to="/products">Continue Shopping</Link>
              </div>
            </main>

            <aside className="cart-summary" aria-label="Cart summary">
              <h2>Cart Summary</h2>
              <dl>
                <div><dt>Subtotal</dt><dd>${formatPrice(subtotal)}</dd></div>
                <div>
                  <dt>Estimated Shipping</dt>
                  <dd>{estimatedShipping === 0 ? "FREE" : `$${formatPrice(estimatedShipping)}`}</dd>
                </div>
                <div className="cart-summary__total">
                  <dt>Estimated Total</dt><dd>${formatPrice(estimatedTotal)}</dd>
                </div>
              </dl>
              <p className="cart-summary__estimate">Final shipping is calculated at checkout.</p>
              <button type="button" className="cart-checkout-button" onClick={handleCheckout}>
                <i className="fas fa-lock" aria-hidden="true"></i> Checkout
              </button>
              <div className="cart-security-note">
                <i className="fas fa-shield-alt" aria-hidden="true"></i>
                <span>Your data will be encrypted and transmitted securely.</span>
              </div>
            </aside>
          </div>
        )}
      </div>
    </article>
  );
};

export default Cart;
